import { Card, Tooltip } from '@radix-ui/themes'
import BulletGraph from '../charts/BulletGraph'
import ColumnChart from '../charts/ColumnChart'
import AreaSpline from '../charts/AreaSpline'
import { useSelector } from 'react-redux'
import { fetchRiskStats, selectStats } from '../../reducers/stats'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import BarChart from '../charts/BarChart'

import { calculateTimeTo } from '../../utils/dates'
import { selectUser } from '../../reducers/users'
import DashboardEmpty from './DashboardEmpty'
import GradientLineChart from '../charts/GradientLineChart'
import {
  getColorForValue,
  getRiskScoreClass,
  rgbRiskGradientStops,
  riskGradientStops,
} from '../../utils/risk-text-colour'
import { IconCircleCheck, IconInfoCircle, IconStopwatch } from '@tabler/icons-react'
import DashboardRiskSkelton from './DashboardRiskSkeleton'
import tableStats from '../../assets/table-stats.png'
import tableStatsDark from '../../assets/table-stats-dark.png'
import tableStatsAlt from '../../assets/table-stats-alt.png'
import tableStatsAltDark from '../../assets/table-stats-alt-dark.png'
import columnChart from '../../assets/column-chart.png'
import columnChartDark from '../../assets/column-chart-dark.png'
import areaSpline from '../../assets/area-spline.png'
import areaSplineDark from '../../assets/area-spline-dark.png'

function DashboardRisk() {
  const di = useAppDispatch()
  const { dashboardRiskStats } = useSelector(selectStats)
  const { darkModeEnabled } = useSelector(selectUser)
  const [countIos, setCountIos] = useState(0)
  const [countAndroid, setCountAndroid] = useState(0)
  const [percentageLatestOsIos, setPercentageLatestOsIos] = useState(0)
  const [percentageLatestOsAndroid, setPercentageLatestOsAndroid] = useState(0)
  const [percentageSmishingIos, setPercentageSmishingIos] = useState(0)
  const [percentageSmishingAndroid, setPercentageSmishingAndroid] = useState(0)
  const [percentageScreenLockIos, setPercentageScreenLockIos] = useState(0)
  const [percentageScreenLockAndroid, setPercentageScreenLockAndroid] = useState(0)
  const [percentageBiometricsIos, setPercentageBiometricsIos] = useState(0)
  const [percentageBiometricsAndroid, setPercentageBiometricsAndroid] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [iosColor, setIosColor] = useState('#5D7CFA')
  const [androidColor, setAndroidColor] = useState('#3AC9DB')
  const [textColor, setTextColor] = useState('#000000')
  const gridColor = darkModeEnabled ? '#60646c' : '#E0E0E0'

  useEffect(() => {
    setIosColor('#5D7CFA')
    setAndroidColor('#3AC9DB')
    if (darkModeEnabled) {
      setTextColor('#FFFFFF')
    } else {
      setTextColor('#000000')
    }
  }, [darkModeEnabled])

  useEffect(() => {
    di(fetchRiskStats())
  }, [])

  useEffect(() => {
    if (dashboardRiskStats) {
      const countIos = dashboardRiskStats.osVersionsIos?.reduce(
        (sum: number, os: { version: string; inDateCount: number; outOfDateCount: number }) =>
          sum + os.inDateCount + os.outOfDateCount,
        0
      )
      setCountIos(countIos)

      const countAndroid = dashboardRiskStats.osVersionsAndroid?.reduce(
        (sum: number, os: { version: string; inDateCount: number; outOfDateCount: number }) =>
          sum + os.inDateCount + os.outOfDateCount,
        0
      )
      setCountAndroid(countAndroid)
      setIsLoading(false)
    }
  }, [dashboardRiskStats])

  useEffect(() => {
    if (dashboardRiskStats) {
      const latestIosOsPercentage = (dashboardRiskStats.protectionMeasures.latestIosOsVersionCount / countIos) * 100
      const latestIosSmishingPercentage =
        (dashboardRiskStats.protectionMeasures.smishingProtectionCountIos / countIos) * 100
      const latestIosScreenLockPercentage = (dashboardRiskStats.protectionMeasures.screenLockCountIos / countIos) * 100
      const latestIosBiometricsPercentage = (dashboardRiskStats.protectionMeasures.biometricsCountIos / countIos) * 100

      setPercentageLatestOsIos(parseFloat(latestIosOsPercentage.toFixed(2)))
      setPercentageSmishingIos(parseFloat(latestIosSmishingPercentage.toFixed(2)))
      setPercentageScreenLockIos(parseFloat(latestIosScreenLockPercentage.toFixed(2)))
      setPercentageBiometricsIos(parseFloat(latestIosBiometricsPercentage.toFixed(2)))
    }
  }, [countIos])

  useEffect(() => {
    if (dashboardRiskStats) {
      const latestAndroidOsPercentage =
        (dashboardRiskStats.protectionMeasures.latestAndroidOsVersionCount / countAndroid) * 100
      const latestAndroidSmishingPercentage =
        (dashboardRiskStats.protectionMeasures.smishingProtectionCountAndroid / countAndroid) * 100
      const latestAndroidScreenLockPercentage =
        (dashboardRiskStats.protectionMeasures.screenLockCountAndroid / countAndroid) * 100
      const latestAndroidBiometricsPercentage =
        (dashboardRiskStats.protectionMeasures.biometricsCountAndroid / countAndroid) * 100

      setPercentageLatestOsAndroid(parseFloat(latestAndroidOsPercentage.toFixed(2)))
      setPercentageSmishingAndroid(parseFloat(latestAndroidSmishingPercentage.toFixed(2)))
      setPercentageScreenLockAndroid(parseFloat(latestAndroidScreenLockPercentage.toFixed(2)))
      setPercentageBiometricsAndroid(parseFloat(latestAndroidBiometricsPercentage.toFixed(2)))
    }
  }, [countAndroid])

  const riskCategories = () => {
    const categories = Array(100).fill('')
    categories[5] = 'Low'
    categories[40] = 'Medium'
    categories[70] = 'High'
    categories[100] = 'Critical'
    return categories
  }

  return (
    <>
      {isLoading ? (
        <DashboardRiskSkelton />
      ) : dashboardRiskStats &&
        (dashboardRiskStats.osVersionsIos?.length > 0 || dashboardRiskStats.osVersionsAndroid?.length > 0) ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 w-full mb-4 border-0 m-0 p-0 bg-contentBackground gap-4">
            <div className="w-full border-0 m-0 p-0 bg-contentBackground flex flex-col gap-4">
              <div>
                <Card
                  size="2"
                  className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full"
                  variant="ghost"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-[150px]">
                    <div className="flex flex-col p-4 flex-grow">
                      <div className="flex gap-1 items-center">
                        <p>Risk Score </p>
                        <Tooltip
                          content="Calculated from Protection Measures (up to 60), OS Version (up to 20), CVE vulnerabilities (up to 40), and Alerts (up to 100). Lower scores reflect stronger protection, fewer vulnerabilities, and no active alerts."
                          side="bottom"
                          align="start"
                          className="max-w-[340px]"
                        >
                          <IconInfoCircle
                            className="cursor-pointer flex-shrink-0 text-gray-500 dark:text-gray-300"
                            size="16"
                          />
                        </Tooltip>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 py-1 flex-grow">All enrolled devices</p>
                      <div className="text-xl">
                        <span
                          style={{ color: getColorForValue(dashboardRiskStats.orgRiskScore) }}
                          className="font-semibold text-6xl"
                        >
                          {dashboardRiskStats.orgRiskScore}
                        </span>
                        /100
                      </div>
                    </div>
                    <div className="flex w-full p-4">
                      <div className="flex w-full pr-4 items-center justify-center">
                        <BulletGraph
                          value={dashboardRiskStats.orgRiskScore}
                          categories={riskCategories()}
                          textColor={textColor}
                          height={80}
                          tickPositions={[5, 40, 70, 100]}
                          stops={rgbRiskGradientStops}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow">
                <Card
                  size="2"
                  className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full"
                  variant="ghost"
                >
                  {dashboardRiskStats.alertsRaisedLast30DaysCount > 0 ? (
                    <div className="flex flex-col justify-between p-4 h-full">
                      <p>Alerts Created</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex-grow">Past 30 days</p>
                      <div className="text-xl">
                        <p className="fot-semibold text-3xl">{dashboardRiskStats.alertsRaisedLast30DaysCount}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 items-center justify-center h-full">
                      <IconCircleCheck className="text-successGreen" />
                      <p className="text-xs font-semibold">No alerts created</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        See how many alerts have been created in the last 30 days
                      </p>
                    </div>
                  )}
                </Card>
                <Card
                  size="2"
                  className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full"
                  variant="ghost"
                >
                  {dashboardRiskStats.timeInMinutesToResolveAlerts > 0 ? (
                    <div className="flex flex-col justify-between p-4 h-full">
                      <p>Avg. time to resolve alerts</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex-grow">Past 30 days</p>
                      <div className="text-xl">
                        <p className="fot-semibold text-3xl">
                          {calculateTimeTo(dashboardRiskStats.timeInMinutesToResolveAlerts)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 items-center justify-center h-full">
                      <IconStopwatch className="text-textTertiary" />
                      <p className="text-xs font-semibold">No alerts resolved</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        See the average time taken to resolve an alert in the last 30 days
                      </p>
                    </div>
                  )}
                </Card>
              </div>
            </div>

            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between pt-4 px-4">
                  <p className="pb-3">Risk Score over time</p>
                  <GradientLineChart
                    height={250}
                    textColor={textColor}
                    showLegend={false}
                    rangeDescription="Last 30 Days"
                    gradientStops={riskGradientStops}
                    gridColor={gridColor}
                    chartData={{
                      categories: [],
                      series: [
                        {
                          name: 'Score',
                          data: dashboardRiskStats.riskScoresOverTime.map((scan) => [scan.date, scan.count]),
                        },
                      ],
                    }}
                  />
                </div>
              </Card>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 w-full mb-4 border-0 m-0 p-0 bg-contentBackground gap-4">
            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between p-4 h-full gap-2">
                  <p>Open Alerts by Severity</p>
                  {dashboardRiskStats.criticalAlertsCount +
                    dashboardRiskStats.highAlertsCount +
                    dashboardRiskStats.suspiciousAlertsCount +
                    dashboardRiskStats.lowAlertsCount >
                  0 ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-4">
                        <div className="flex flex-col border rounded p-3 flex items-center justify-center dark:border-neutral-11">
                          <div className="text-xs">Critical</div>
                          <div className="text-3xl">{dashboardRiskStats.criticalAlertsCount}</div>
                        </div>
                        <div className="flex flex-col border rounded p-3 flex items-center justify-center dark:border-neutral-11">
                          <div className="text-xs">High</div>
                          <div className="text-3xl">{dashboardRiskStats.highAlertsCount}</div>
                        </div>
                        <div className="flex flex-col border rounded p-3 flex items-center justify-center dark:border-neutral-11">
                          <div className="text-xs">Suspicious</div>
                          <div className="text-3xl">{dashboardRiskStats.suspiciousAlertsCount}</div>
                        </div>
                        <div className="flex flex-col border rounded p-3 flex items-center justify-center dark:border-neutral-11">
                          <div className="text-xs">Low</div>
                          <div className="text-3xl">{dashboardRiskStats.lowAlertsCount}</div>
                        </div>
                      </div>
                      <ColumnChart
                        chartData={{
                          seriesName: 'Alerts',
                          textColor: textColor,
                          gridColor: gridColor,
                          categories: ['Critical', 'High', 'Suspicious', 'Low'],
                          data: [
                            dashboardRiskStats.criticalAlertsCount,
                            dashboardRiskStats.highAlertsCount,
                            dashboardRiskStats.suspiciousAlertsCount,
                            dashboardRiskStats.lowAlertsCount,
                          ],
                          colors: ['#C92A2A', '#FA5252', '#FD7E14', '#FAB007'],
                          height: 200,
                        }}
                      />
                    </>
                  ) : (
                    <div className="flex flex-col gap-2 mt-4 items-center justify-center h-full min-h-[200px]">
                      <IconCircleCheck className="text-successGreen" />
                      <p className="text-xs font-semibold">No open alerts</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        See the volume of open alerts across your devices
                      </p>
                      <img src={columnChart} alt="Table Stats" className="px-1.5 dark:hidden absolute" />
                      <img src={columnChartDark} alt="Table Stats Dark" className="px-1.5 hidden dark:block absolute" />
                    </div>
                  )}
                </div>
              </Card>
            </div>
            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between p-4 h-full">
                  <p>Alerts created over time on active devices</p>
                  {dashboardRiskStats.alertsOverTimeIOS.reduce(
                    (sum: number, alert: { date: string; count: number }) => sum + alert.count,
                    0
                  ) +
                    dashboardRiskStats.alertsOverTimeAndroid.reduce(
                      (sum: number, alert: { date: string; count: number }) => sum + alert.count,
                      0
                    ) >
                  0 ? (
                    <AreaSpline
                      chartData={{
                        showLegend: true,
                        gridColor: gridColor,
                        series: [
                          {
                            name: 'iOS',
                            color: iosColor,
                            data: dashboardRiskStats.alertsOverTimeIOS.map((alert) => [alert.date, alert.count]),
                          },
                          {
                            name: 'Android',
                            color: androidColor,
                            data: dashboardRiskStats.alertsOverTimeAndroid.map((alert) => [alert.date, alert.count]),
                          },
                        ],
                        height: 350,
                        textColor: textColor,
                      }}
                    />
                  ) : (
                    <div className="flex flex-col gap-2 mt-4 items-center justify-center h-full min-h-[200px]">
                      <IconCircleCheck className="text-successGreen" />
                      <p className="text-xs font-semibold">No alerts created</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        See how the number of alerts created changes over time
                      </p>
                      <img src={areaSpline} alt="Table Stats" className="px-1.5 dark:hidden absolute" />
                      <img src={areaSplineDark} alt="Table Stats Dark" className="px-1.5 hidden dark:block absolute" />
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 w-full mb-4 border-0 m-0 p-0 bg-contentBackground gap-4">
            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between p-4 h-full">
                  <p>Percentage of Protection Measures</p>
                  <BarChart
                    chartData={{
                      textColor: textColor,
                      gridColor: gridColor,
                      categories: ['Latest OS', 'Smishing', 'Screen Lock', 'Biometrics'],
                      series: [
                        {
                          name: 'iOS',
                          color: iosColor,
                          data: [
                            {
                              y: percentageLatestOsIos,
                              raw: dashboardRiskStats.protectionMeasures.latestIosOsVersionCount,
                            },
                            {
                              y: percentageSmishingIos,
                              raw: dashboardRiskStats.protectionMeasures.smishingProtectionCountIos,
                            },
                            {
                              y: percentageScreenLockIos,
                              raw: dashboardRiskStats.protectionMeasures.screenLockCountIos,
                            },
                            {
                              y: percentageBiometricsIos,
                              raw: dashboardRiskStats.protectionMeasures.biometricsCountIos,
                            },
                          ],
                        },
                        {
                          name: 'Android',
                          color: androidColor,
                          data: [
                            {
                              y: percentageLatestOsAndroid,
                              raw: dashboardRiskStats.protectionMeasures.latestAndroidOsVersionCount,
                            },
                            {
                              y: percentageSmishingAndroid,
                              raw: dashboardRiskStats.protectionMeasures.smishingProtectionCountAndroid,
                            },
                            {
                              y: percentageScreenLockAndroid,
                              raw: dashboardRiskStats.protectionMeasures.screenLockCountAndroid,
                            },
                            {
                              y: percentageBiometricsAndroid,
                              raw: dashboardRiskStats.protectionMeasures.biometricsCountAndroid,
                            },
                          ],
                        },
                      ],
                    }}
                  />
                </div>
              </Card>
            </div>
            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col p-4 gap-2 h-full min-h-[350px]">
                  <p>Highest Risk Devices</p>

                  {dashboardRiskStats.highRiskDevices.length === 0 ? (
                    <div className="flex flex-col gap-2 items-center justify-center h-full min-h-[200px]">
                      <IconCircleCheck className="text-successGreen" />
                      <p className="text-xs font-semibold">No Devices at Risk</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        No devices have a risk score greater than 0
                      </p>
                      <img src={tableStats} alt="Table Stats" className="px-1.5 dark:hidden absolute" />
                      <img src={tableStatsDark} alt="Table Stats Dark" className="px-1.5 hidden dark:block absolute" />
                    </div>
                  ) : (
                    <table className="table-auto w-full text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-neutral-11 font-semibold text-gray-400 text-left dark:text-gray-200">
                          <th className="text-left p-2">Device Name</th>
                          <th className="text-left p-2 float-right">Risk Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardRiskStats.highRiskDevices.map((device, index) => (
                          <tr key={index} className="border-b border-gray-200 dark:border-neutral-11">
                            <td className="p-2">{device.deviceName}</td>
                            <td className="p-2 float-right">
                              <span className={`${getRiskScoreClass(device.riskScore)} font-semibold`}>
                                {device.riskScore}
                              </span>
                              <span className="text-gray-400 dark:text-gray-200 px-1">/&nbsp;100</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </Card>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 w-full mb-4 border-0 m-0 p-0 bg-contentBackground gap-4">
            <div className="w-full border-0 m-0 p-0 bg-contentBackground min-h-64">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col p-4 gap-2 h-full min-h-[350px]">
                  <p>Common Vulnerabilities and Exposures</p>
                  {dashboardRiskStats.cveList.length === 0 ? (
                    <div className="flex flex-col gap-2 mt-4 items-center justify-center h-full min-h-[200px]">
                      <IconCircleCheck className="text-successGreen" />
                      <p className="text-xs font-semibold">No CVE’s detected</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        No devices are running OS Versions with known CVE’s
                      </p>
                      <img src={tableStatsAlt} alt="Table Stats" className="px-1.5 dark:hidden absolute" />
                      <img
                        src={tableStatsAltDark}
                        alt="Table Stats Dark"
                        className="px-1.5 hidden dark:block absolute"
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="overflow-auto max-h-[350px]">
                        <table className="table-auto w-full text-xs border-collapse">
                          <thead className="sticky top-0 bg-cardFill z-10">
                            <tr className="border-b border-gray-200 dark:border-neutral-11 font-semibold text-gray-400 text-left dark:text-gray-200">
                              <th className="p-2">CVE Name</th>
                              <th className="p-2">OS</th>
                              <th className="p-2">OS Patch</th>
                              <th className="p-2">Devices</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboardRiskStats.cveList?.map((cve, index) => (
                              <tr key={index} className="border-b border-gray-200 dark:border-neutral-11">
                                <td className="p-2">
                                  <a
                                    className="text-mainPurple dark:text-secondaryPurple cursor-pointer hover:underline"
                                    href={`https://www.cve.org/CVERecord?id=${cve.cveId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {cve.cveId}
                                  </a>
                                </td>
                                <td className="p-2">{cve.os}</td>
                                <td className="p-2">{cve.osPatchVersion}</td>
                                <td className="p-2">{cve.devices}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </>
      ) : (
        <DashboardEmpty />
      )}
    </>
  )
}

export default DashboardRisk
